import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Text,
    Link,
    Image,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useIntl, FormattedNumber, FormattedMessage } from 'react-intl'
import {useBrand} from '../../../contexts/brand-context'

/* Hooks */
import { useCurrency } from '@salesforce/retail-react-app/app/hooks'

/* Icons */
import { StandardShipping, ExpressShipping, UberShippingBlack, InStoreShipping } from '../../../components/icons'
import {resolveGDIStyles, GDI_TEXT_BLOCK_FIELD_MAP} from '../../../components/optimizely/gdi/utils/gdi-styles'
import {renderLayoutsSwitch} from '../../../components/optimizely/utils/contentRenderer'

const DeliveryOptions = ({ shippingMethodsData = {}, cmsDeliverySectionBlock, disableShowMore = false }) => {
    const styles = useMultiStyleConfig('DeliveryOptions')
    const { formatMessage } = useIntl()
    const {isGarage} = useBrand()
    const { currency } = useCurrency()
    const [shippingMethods, setShippingMethods] = useState({})
    const [smLimit, setSmLimit] = useState(null)
    const [initialSmLimit, setInitialSmLimit] = useState(null)
    const [showMore, setShowMore] = useState(true)
    const [smCMSResourceMapping, setSmCMSResourceMapping] = useState(null)

    /* In case new shipping methods will be returned from the API this object must be updated accordingly */
    const smResourceMapping = {
        "standard": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.standardMethod',
                defaultMessage: 'Standard Shipping'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => formatMessage({
                id: 'pdp_product_detail.sm.description.standardMethod',
                defaultMessage: 'FREE for orders $60 & up'
            }),
            cost: 0,
            icon: () => (<StandardShipping />)
        },
        "express": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.expressMethod',
                defaultMessage: 'Express Shipping'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => '',
            cost: 10,
            icon: () => (<ExpressShipping width={'36px'} />)
        },
        "priority": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.priorityMethod',
                defaultMessage: 'Priority Shipping'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => '',
            cost: 20,
            icon: () => (<ExpressShipping width={'36px'} />)
        },
        "accelerated": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.acceleratedGround',
                defaultMessage: 'Accelerated Ground'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => '',
            cost: 7,
            icon: () => (<ExpressShipping width={'36px'} />)
        },
        "uber": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.uber',
                defaultMessage: 'Same-Day Shipping*'
            }),
            arrival: () => {
                return isGarage ? (
                    <FormattedMessage
                        id="pdp_product_detail.sm.arrival.uber.garage"
                        defaultMessage="<span>Get it today!</span>"
                        values={{
                            span: (chunks) => <span style={{fontWeight: 'bold'}}>{chunks}</span>
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="pdp_product_detail.sm.arrival.uber.dynamite"
                        defaultMessage="<span>Get it today!</span>"
                        values={{
                            span: (chunks) => <span style={{fontWeight: 'bold'}}>{chunks}</span>
                        }}
                    />
                )
            },
            description: () => formatMessage({
                id: 'pdp_product_detail.sm.description.uber',
                defaultMessage: 'For orders placed by 3:00 p.m.'
            }),
            cost: 10,
            icon: () => (<UberShippingBlack />)
        },
        "same_day_pickup": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.pick_up_in_store',
                defaultMessage: 'Same-Day Pickup in Store**'
            }),
            arrival: () => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival.pick_up_in_store"
                    defaultMessage="<span>Available within 2 hours</span>"
                    values={{
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => formatMessage({
                id: 'pdp_product_detail.sm.description.pick_up_in_store',
                defaultMessage: 'For orders placed by 3:00 p.m.'
            }),
            cost: 0,
            icon: () => (<InStoreShipping />)
        }
    }

    const freeLabel = formatMessage({
        defaultMessage: 'Free',
        id: 'checkout_confirmation.label.free'
    })

    const replaceDeliveryRange = (value = '', low = '', high = '') =>
        value?.replace('{low}-{high} business days', `<span style="font-weight: bold;">${low}-${high} business days</span>`)
            ?.replace('{low}-{high}', `<span style="font-weight: bold;">${low}-${high}</span>`)
            ?.replace('{low}', `<span style="font-weight: bold;">${low}</span>`)
            ?.replace('{high}', `<span style="font-weight: bold;">${high}</span>`)

    const getCMSDeliveryOptionsList = (cmsBlock) =>
        cmsBlock?.DeliveryOptions || []

    const getCMSNotesList = (cmsBlock) =>
        Array.isArray(cmsBlock?.GDIPDPPageNotes) ? cmsBlock.GDIPDPPageNotes : []

    const cmsNotes = getCMSNotesList(cmsDeliverySectionBlock)

    const getCMSAssetUrl = (asset) => asset?.Url || asset?.Expanded?.Url

    /* DeliveryKey is authored as the shipping-method id returned by the delivery-options
     * API, so a method the API starts returning becomes CMS-overridable without a code
     * change. Unmatched or duplicate keys keep the hardcoded smResourceMapping entry. */
    const generateDeliveryOptionsFromCMS = (filteredShippingMethods, deliveryOptionList) => {
        const result = {}

        if (!Array.isArray(deliveryOptionList)) return result

        deliveryOptionList.forEach((option) => {
            const block = option?.ContentLink?.Expanded
            if (!block) return

            const key = block.DeliveryKey
            if (!key || !(key in filteredShippingMethods)) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(
                        `[DeliveryOptions] CMS DeliveryKey "${key}" matches no shipping method. Expected one of: ${Object.keys(filteredShippingMethods).join(', ') || '(none returned)'}`
                    )
                }
                return
            }
            if (result[key]) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(
                        `[DeliveryOptions] Duplicate CMS DeliveryKey "${key}" — keeping the first block.`
                    )
                }
                return
            }

            const cmsPriceRaw = typeof block.DeliveryPrice === 'string'
                ? block.DeliveryPrice.trim()
                : block.DeliveryPrice
            const hasCMSCost = cmsPriceRaw !== '' && cmsPriceRaw !== null && cmsPriceRaw !== undefined

            result[key] = {
                ...smResourceMapping[key],
                isCMS: true,
                name: block.GDIPDPDeliveryOptionsBlockTitle,
                arrival: (low, high) => replaceDeliveryRange(block.DescriptionFirstLine, low, high),
                description: () => block.DescriptionSecondLine,
                ...(hasCMSCost ? {cost: String(cmsPriceRaw), hasCMSCost: true} : {}),
                icon: () => (
                    <Image
                        margin={'0 auto'}
                        src={getCMSAssetUrl(block.Icon)}
                        alt={block?.IconMediaAltText || block?.GDIPDPDeliveryOptionsBlockTitle || ''}
                    />
                )
            }
        })

        return result
    }

    const getCMSStyles = (styleConfig, fallback) => ({
        ...fallback,
        ...resolveGDIStyles(styleConfig, GDI_TEXT_BLOCK_FIELD_MAP).styles
    })

    const nameStyles = getCMSStyles(cmsDeliverySectionBlock?.GDIPDPPageTitleStyle, styles.name)
    const priceStyles = getCMSStyles(cmsDeliverySectionBlock?.GDIPDPPagePriceStyle, styles.cost)
    const arrivalStyles = getCMSStyles(cmsDeliverySectionBlock?.DescriptionLine1Style, styles.typo)
    const descriptionStyles = getCMSStyles(cmsDeliverySectionBlock?.DescriptionLine2Style, styles.typo)

    useEffect(() => {
        // Filter shippingMethods to include only those present in smResourceMapping
        // If new delivery option will be returned from BD the smResourceMapping must be updated accordingly
        if (shippingMethodsData) {
            const filteredShippingMethods = Object.fromEntries(
                Object.entries(shippingMethodsData).filter(([key]) => key in smResourceMapping)
            );

            setShippingMethods(filteredShippingMethods)
        }
    }, [shippingMethodsData])

    useEffect(() => {
        if (shippingMethods && cmsDeliverySectionBlock) {
            const cmsDeliveryOptions = generateDeliveryOptionsFromCMS(
                shippingMethods,
                getCMSDeliveryOptionsList(cmsDeliverySectionBlock)
            )
            setSmLimit(2)
            setInitialSmLimit(2)
            setShowMore(Object.keys(shippingMethods).length > 2)
            setSmCMSResourceMapping(Object.keys(cmsDeliveryOptions).length ? cmsDeliveryOptions : null)
        } else {
            setSmLimit(2)
            setInitialSmLimit(2)
            setSmCMSResourceMapping(null)
        }
    }, [cmsDeliverySectionBlock, shippingMethods])

    return (
        shippingMethodsData &&
        <Box>
            {Object.keys(shippingMethodsData)?.slice(0, disableShowMore ? Object.keys(shippingMethodsData)?.length : (smLimit > 0 ? smLimit : Object.keys(shippingMethodsData)?.length))?.map((key) => {
                const smData = shippingMethods[key]
                const sm = smCMSResourceMapping?.[key] || smResourceMapping[key]
                const displayCost = sm?.hasCMSCost
                    ? sm.cost
                    : (smData?.hasOwnProperty('cost') ? smData.cost : sm?.cost)

                return (sm && smData &&
                    <Box key={key} {...styles.container}>
                        <Box {...styles.icon}>
                            {sm.icon()}
                        </Box>

                        <Box>
                            <Text as="h4" {...nameStyles}>
                                {sm.name}
                            </Text>
                            {sm.isCMS ? (
                                <Text
                                    {...arrivalStyles}
                                    dangerouslySetInnerHTML={{ __html: sm.arrival(smData?.low || '', smData?.high || '') }}
                                />
                            ) : (
                                <Text {...arrivalStyles} >
                                    {sm.arrival(smData?.low || '', smData?.high || '')}
                                </Text>
                            )}
                            {sm.isCMS ? (
                                <Text
                                    {...descriptionStyles}
                                    dangerouslySetInnerHTML={{ __html: sm.description() }}
                                />
                            ) : (
                                <Text {...descriptionStyles}>
                                    {sm.description()}
                                </Text>
                            )}
                        </Box>

                        <Box {...styles.costWrapper}>
                            <Text {...priceStyles}>
                                {displayCost === 0 ? (
                                    freeLabel
                                ) : (
                                    typeof displayCost === 'string' ? (
                                        displayCost
                                    ) : (
                                        <FormattedNumber
                                            value={displayCost}
                                            style="currency"
                                            currency={currency}
                                        />
                                    )
                                )}
                            </Text>
                        </Box>
                    </Box>
                )
            })}

            {(!showMore || disableShowMore) &&
                <Box mb={{ base: 4 }}>
                    {cmsNotes.length > 0 ? (
                        cmsNotes.map((note, index) => {
                            const block = note?.ContentLink?.Expanded
                            if (!block) return null

                            const hasCMSStyles = Array.isArray(block?.GDITextBlockStylesPerViewType) &&
                                block.GDITextBlockStylesPerViewType.length > 0

                            return (
                                <React.Fragment key={block?.ContentLink?.GuidValue || index}>
                                    {renderLayoutsSwitch(block, hasCMSStyles ? undefined : styles.typo)}
                                </React.Fragment>
                            )
                        })
                    ) : (
                        <>
                            <Text {...styles.typo}>
                                <FormattedMessage
                                    defaultMessage="* Select locations & items only"
                                    id="product_detail.shipping_methods.text1"
                                />
                            </Text>

                            <Text {...styles.typo}>
                                <FormattedMessage
                                    defaultMessage="** Select items only"
                                    id="product_detail.shipping_methods.text2"
                                />
                            </Text>
                        </>
                    )}
                </Box>
            }

            {!disableShowMore && (showMore ? (
                <Button variant={'link'}
                    as={Link}
                    href='#'
                    aria-label='See more content from the Shipping & Pickup Options'
                    onClick={(e) => {
                        if (e) e.preventDefault()
                        setSmLimit(Object.keys(shippingMethods).length)
                        setShowMore(false)
                    }}
                    {...styles.typo}>
                    <FormattedMessage
                        defaultMessage="See More"
                        id="product_detail.shipping_methods.see_more"
                    />
                </Button>
            ) : (
                smLimit > 0 && (
                    <Button variant={'link'}
                        as={Link}
                        href='#'
                        aria-label='See less content from the Shipping & Pickup Options'
                        onClick={(e) => {
                            if (e) e.preventDefault()
                            setSmLimit(initialSmLimit)
                            setShowMore(true)
                        }}
                        {...styles.typo}>
                        <FormattedMessage
                            defaultMessage="See Less"
                            id="product_detail.shipping_methods.see_less"
                        />
                    </Button>
                )
            ))}
        </Box>
    )
}

DeliveryOptions.propTypes = {
    shippingMethodsData: PropTypes.object.isRequired,
    cmsDeliverySectionBlock: PropTypes.object,
    disableShowMore: PropTypes.bool
}

export default DeliveryOptions
